import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useConsent } from './providers/ConsentProvider.jsx';
import { useSession } from './providers/SessionProvider.jsx';
import { useAccess } from './providers/AccessProvider.jsx';
import { loadMembershipPlans } from '../services/account/accountService.js';
import {
  captureOpenAICampaignAttribution,
  getOpenAICampaignAttribution,
  getOpenAICampaignStep,
  hasOpenAICampaignAttribution,
  markOpenAICampaignStep,
  measureOpenAIEventOnce,
  sendOpenAIServerEvents,
  syncOpenAIMeasurementConsent,
} from '../services/ads/openaiMeasurement.js';

const FALLBACK_PLANS = {
  'svod-monthly': { planId: 'svod-monthly', name: 'WatchVIM Monthly', amount: 599, currency: 'USD' },
  'svod-annual': { planId: 'svod-annual', name: 'WatchVIM Annual', amount: 4599, currency: 'USD' },
};

function safeEventToken(value) {
  return String(value || '').replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 180) || 'watchvim';
}

async function resolvePlan(planKey) {
  const key = planKey === 'svod-annual' ? 'svod-annual' : 'svod-monthly';
  const fallback = FALLBACK_PLANS[key];
  try {
    const rows = await loadMembershipPlans();
    const id = key === 'svod-annual' ? 'annual' : 'monthly';
    const plan = rows.find((row) => String(row.id || '').toLowerCase() === id);
    if (!plan) return fallback;
    const price = Number(plan.price_usd ?? plan.price);
    return {
      planId: key,
      name: String(plan.name || fallback.name),
      amount: Number.isFinite(price) && price >= 0 ? Math.round(price * 100) : fallback.amount,
      currency: String(plan.currency || 'USD').toUpperCase(),
    };
  } catch (_) {
    return fallback;
  }
}

function contentsData(plan) {
  return {
    type: 'contents',
    amount: plan.amount,
    currency: plan.currency,
    contents: [{
      id: plan.planId,
      name: plan.name,
      content_type: 'plan',
      quantity: 1,
      amount: plan.amount,
      currency: plan.currency,
    }],
  };
}

function enrollmentData(plan) {
  return {
    type: 'plan_enrollment',
    plan_id: plan.planId,
    amount: plan.amount,
    currency: plan.currency,
    contents: [{
      id: plan.planId,
      name: plan.name,
      content_type: 'plan',
      quantity: 1,
    }],
  };
}

export function OpenAIAdsMeasurement() {
  const location = useLocation();
  const { preferences, hasAdvertisingConsent } = useConsent();
  const { session, user } = useSession();
  const access = useAccess();

  useEffect(() => {
    syncOpenAIMeasurementConsent(hasAdvertisingConsent).catch(() => {});
  }, [hasAdvertisingConsent, preferences.updatedAt]);

  useEffect(() => {
    if (!hasAdvertisingConsent) return;
    captureOpenAICampaignAttribution();
    if (!hasOpenAICampaignAttribution()) return;

    if (location.pathname === '/subscribe') {
      const attribution = getOpenAICampaignAttribution();
      const eventId = `page_subscribe_${safeEventToken(attribution.oppref || location.key)}`;
      measureOpenAIEventOnce('page_viewed', {
        type: 'contents',
        contents: [{ id: 'watchvim_subscribe', name: 'WatchVIM Subscriber Campaign', content_type: 'page' }],
      }, { eventId }).catch(() => {});
    }

    if (location.pathname === '/signup') {
      const existing = getOpenAICampaignStep('registration_started');
      const eventId = existing || `registration_started_${safeEventToken(location.key)}_${Date.now()}`;
      if (!existing) markOpenAICampaignStep('registration_started', eventId);
      measureOpenAIEventOnce('custom', { type: 'custom' }, {
        eventId,
        customEventName: 'registration_started',
      }).catch(() => {});
    }

    if (location.pathname === '/checkout/membership' && user?.id && !access.subscription.active) {
      const params = new URLSearchParams(location.search || '');
      const planKey = params.get('plan') === 'svod-annual' ? 'svod-annual' : 'svod-monthly';
      let eventId = getOpenAICampaignStep('checkout_started');
      if (!eventId) {
        eventId = `checkout_${safeEventToken(user.id)}_${Date.now()}`;
        markOpenAICampaignStep('checkout_started', eventId);
        markOpenAICampaignStep('checkout_plan', planKey);
      }
      resolvePlan(planKey).then((plan) => measureOpenAIEventOnce('checkout_started', contentsData(plan), { eventId })).catch(() => {});
    }
  }, [location.pathname, location.search, location.key, hasAdvertisingConsent, user?.id, access.subscription.active]);

  useEffect(() => {
    if (!hasAdvertisingConsent || !user?.id || !hasOpenAICampaignAttribution()) return;
    if (!getOpenAICampaignStep('registration_started')) return;
    const eventId = `registration_${safeEventToken(user.id)}`;
    measureOpenAIEventOnce('registration_completed', { type: 'customer_action' }, { eventId })
      .then(() => markOpenAICampaignStep('registration_completed', eventId))
      .catch(() => {});
  }, [hasAdvertisingConsent, user?.id]);

  useEffect(() => {
    if (!hasAdvertisingConsent || !session?.access_token || !user?.id) return;
    if (!access.subscription.active || !hasOpenAICampaignAttribution()) return;
    if (!getOpenAICampaignStep('checkout_started')) return;

    const paypalSubscriptionId = String(access.subscription.paypalSubscriptionId || '').trim();
    if (!paypalSubscriptionId) return;
    const serverMarker = `conversion_server_${safeEventToken(paypalSubscriptionId)}`;
    if (getOpenAICampaignStep(serverMarker) === 'sent') return;

    const planKeyRaw = String(access.subscription.plan || getOpenAICampaignStep('checkout_plan') || '').toLowerCase();
    const planKey = planKeyRaw.includes('annual') || planKeyRaw.includes('year') ? 'svod-annual' : 'svod-monthly';
    const subscriptionEventId = `subscription_${safeEventToken(paypalSubscriptionId)}`;
    const orderEventId = `order_${safeEventToken(paypalSubscriptionId)}`;

    resolvePlan(planKey).then(async (plan) => {
      const subscriptionData = enrollmentData(plan);
      const orderData = contentsData(plan);

      await Promise.allSettled([
        measureOpenAIEventOnce('subscription_created', subscriptionData, { eventId: subscriptionEventId }),
        measureOpenAIEventOnce('order_created', orderData, { eventId: orderEventId }),
      ]);

      try {
        const result = await sendOpenAIServerEvents({
          session,
          events: [
            { id: subscriptionEventId, type: 'subscription_created', data: subscriptionData },
            { id: orderEventId, type: 'order_created', data: orderData },
          ],
        });
        if (result?.ok && !result?.skipped) markOpenAICampaignStep(serverMarker, 'sent');
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[OpenAI Ads] server conversion relay retryable:', error?.message || error);
      }
    }).catch(() => {});
  }, [
    hasAdvertisingConsent,
    session?.access_token,
    user?.id,
    access.subscription.active,
    access.subscription.plan,
    access.subscription.paypalSubscriptionId,
  ]);

  return null;
}
